/** Drawing text in the 8 x 8 font: a white glyph atlas, tinted copies cached per colour. */

import { makeCanvas } from './canvas';
import { LETTERS } from './fontLetters';
import { SYMBOLS } from './fontSymbols';
import { colour as paletteColour } from './palette';

export const GLYPH = 8;
const PER_ROW = 16;

const GLYPHS: Readonly<Record<string, string>> = { ...LETTERS, ...SYMBOLS };
const INDEX = new Map<string, number>(Object.keys(GLYPHS).map((ch, i) => [ch, i]));

/** A colour: a palette index (render/palette.ts), or any CSS colour. */
export type Colour = number | string;

export function css(colour: Colour): string {
  return typeof colour === 'number' ? paletteColour(colour) : colour;
}

let white: HTMLCanvasElement | null = null;
const tinted = new Map<string, HTMLCanvasElement>();

function whiteAtlas(): HTMLCanvasElement {
  if (white) return white;
  const chars = Object.keys(GLYPHS);
  const rows = Math.ceil(chars.length / PER_ROW);
  const canvas = makeCanvas(PER_ROW * GLYPH, rows * GLYPH);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('no 2d context');
  const img = ctx.createImageData(canvas.width, canvas.height);
  chars.forEach((ch, i) => {
    const gx = (i % PER_ROW) * GLYPH;
    const gy = Math.floor(i / PER_ROW) * GLYPH;
    (GLYPHS[ch] ?? '').split('|').forEach((row, y) => {
      for (let x = 0; x < GLYPH; x++) {
        if (row[x] !== '#') continue;
        const o = ((gy + y) * canvas.width + gx + x) * 4;
        img.data.set([255, 255, 255, 255], o);
      }
    });
  });
  ctx.putImageData(img, 0, 0);
  white = canvas;
  return canvas;
}

function atlasFor(colour: string): HTMLCanvasElement {
  const hit = tinted.get(colour);
  if (hit) return hit;
  const base = whiteAtlas();
  const canvas = makeCanvas(base.width, base.height);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('no 2d context');
  ctx.drawImage(base, 0, 0);
  ctx.globalCompositeOperation = 'source-in';
  ctx.fillStyle = colour;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  tinted.set(colour, canvas);
  return canvas;
}

/** Lower case is drawn as upper case; unknown characters as `?`. */
export function glyphIndex(ch: string): number {
  return INDEX.get(ch) ?? INDEX.get(ch.toUpperCase()) ?? INDEX.get('?') ?? 0;
}

export function hasGlyph(ch: string): boolean {
  return INDEX.has(ch) || INDEX.has(ch.toUpperCase());
}

/** Draw `text` with its top-left at (x, y), each glyph `scale` times its size. */
export function drawText(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, colour: Colour, scale = 1): void {
  const atlas = atlasFor(css(colour));
  const size = GLYPH * scale;
  let cx = Math.round(x);
  for (const ch of text) {
    if (ch !== ' ') {
      const i = glyphIndex(ch);
      ctx.drawImage(atlas, (i % PER_ROW) * GLYPH, Math.floor(i / PER_ROW) * GLYPH, GLYPH, GLYPH, cx, Math.round(y), size, size);
    }
    cx += size;
  }
}

export function textWidth(text: string, scale = 1): number {
  return [...text].length * GLYPH * scale;
}
